// 用户类型
export type UserType = 'admin' | 'student' | 'teacher';

export enum UserTypeEnum {
  Admin = 'admin',
  Student = 'student',
  Teacher = 'teacher',
}

// 登录用户类型集合
export const LoginUserTypes: UserType[] = ['student', 'teacher'];

export type Role = 'ADMIN' | 'STUDENT' | 'TEACHER';

export enum RoleEnum {
  Admin = 'ADMIN',
  Student = 'STUDENT',
  Teacher = 'TEACHER',
}

export type Source = 'ADMIN' | 'STUDENT' | 'TEACHER';

export type Status = 'ACTIVE' | 'SUSPENDED';

export enum StatusEnum {
  Active = 'ACTIVE',
  Suspended = 'SUSPENDED',
}

export type CourseStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'PUBLISHED';

export enum CouseStatusEnum {
  Draft = 'DRAFT',
  Pending = 'PENDING',
  Approved = 'APPROVED',
  Published = 'PUBLISHED',
}

export type SubmittedStatus = 'submitted' | 'late' | 'missing';

export enum SubmittedStatusEnum {
  Submitted = 'submitted',
  Late = 'late',
  Missing = 'missing',
}

export type Category =
  | 'Assignments'
  | 'MidtermExam'
  | 'FinalExam'
  | 'Participation';

export enum CategoryEnum {
  Assignments = 'Assignments',
  MidtermExam = 'MidtermExam',
  FinalExam = 'FinalExam',
  Participation = 'Participation',
}

export type UnitType = 'Discussion' | 'Document';

export enum UnitTypeEnum {
  Discussion = 'Discussion',
  Document = 'Document',
}

export type AssignmentType = 'pdf' | 'doc' | 'docx' | 'ipynb' | 'py' | 'txt';

export enum AssignmentTypeEnum {
  Pdf = 'pdf',
  Doc = 'doc',
  Docx = 'docx',
  Ipynb = 'ipynb',
  Py = 'py',
  Txt = 'txt',
}

// 用户
export interface User {
  id?: number;
  avatar?: string;
  username: string;
  full_name: string;
  password?: string;
  email: string;
  role: Role;
  source: Source;
  status: Status;
  department?: string;
  major?: string;
  gender?: string;
  bio?: string;
  phone?: string;
  updated_at?: string;
  created_at?: string;
  last_login_at?: string;
  assigned_course_num: number;
}

export interface Teacher extends User {
  teacher_title?: string;
  teacher_intro?: string;
  teacher_photo?: string;
}

export interface Student extends User {
  student_title?: string;
  student_intro?: string;
  student_photo?: string;
}

export interface CourseRecentActivityItem {
  type:
    | 'announcement_comment'
    | 'discussion_reply'
    | 'assignment_submission'
    | 'exam_submission'
    | string;
  id: number;
  item_id?: number;
  title?: string;
  actor_id?: number;
  actor_role?: string;
  actor_name?: string;
  actor_avatar?: string;
  content?: string;
  created_at: string;
}

// 课程
export interface Course {
  id?: number;
  title: string;
  description: string;
  status: CourseStatus;
  cover_url: string;
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  modules: Module[];
  teachers?: Teacher[];
  students?: Student[];
  recent_activity?: CourseRecentActivityItem[];
  topTeacher: string;
  totalModules: number;
}

export interface Enrollment {
  id: number;
  course_id: number;
  student_id: number;
  enrolled_at: string;
}

export interface Week {
  id?: number;
  lessons: Unit[];
}

export interface Module {
  module_number: number;
  title: string;
  cover_url: string;
  id: number;
  completedItems: number;
  progressPercent: number;
  units: Unit[];
  assignments: Assignment[];
}

// 课程单元
export interface Unit {
  module_id: number;
  unit_number: number;
  title: string;
  content: string;
  videoUrl: string;
  order_index: number;
  materials: string[];
  cover_url: string;
  id: number;
  course_id: number;
  hours?: number;
  views: number;
  unit_type: UnitType;
}

export interface Assignment {
  unit_id: number;
  course_id: number;
  category: string;
  title: string;
  description: string;
  dueDate: string;
  points: number;
  grade?: number;
  id: number;
  submission?: string;
  created_at: string;
  points: number;
}

export interface Announcement {
  course_id: number;
  title: string;
  content: string;
  allow_replies: boolean;
  is_pinned: boolean;
  id: number;
  creator_id: number;
  creator_name: string;
  created_at: string;
  updated_at: string;
}

export interface Module {
  id: number;
  course_id: number;
  module_number: number;
  title?: string;
  cover_url?: string;
}

// 菜单
export interface Menu {
  // 显示菜单名称
  name: string;
  // 菜单图标
  icon?: string;
  // 菜单router路径
  path?: string;
  // 点击时是否折叠/展开sidebar
  collapse?: boolean;
}

export interface Submission {
  content: string;
  downloadUrl: string;
  feedback?: string;
  fileUrl: string;
  filename?: string;
  files?: SubmissionFile[];
  gradedAt?: string;
  id: number;
  score?: number;
  submittedAt: string;
}

export interface SubmissionFile {
  fileUrl: string;
  fileName?: string;
  downloadUrl?: string;
}

export interface Comment {
  content: string;
  id: number;
  announcement_id: number;
  author_id: number;
  author_name: string;
  created_at: string;
  updated_at: string;
}

export interface Grade {
  submission_id: number;
  item_id: number;
  type: SubmissionItemType;
  title: string;
  score: number;
  points: number;
  graded_at: string;
  feedback: string;
  unit_id: number;
}

export type SubmissionItemType = 'assignment' | 'exam';

export enum SubmissionItemTypeEnum {
  Assignment = 'assignment',
  Exam = 'exam',
}

export interface UploadData {
  bucket: string;
  key: string;
  object_url: string;
  upload_url: string;
  download_url: string;
  expires_in: number;
  method: string;
  content_type: string;
  asset_type: string;
  size_bytes: number;
}

export interface UploadParams {
  filename: string;
  size_bytes: number;
  asset_type: string;
  content_type: string;
}

export interface Message {
  id: string;
  text: string;
  sender: string;
  timestamp: string;
  /**
   * Raw datetime for sorting / grouping (e.g. API `created_at`).
   * `timestamp` is display-only (often a formatted time like "11:00 PM").
   */
  createdAt?: string;
  isOwn?: boolean;
}

export interface Conversation {
  id: string;
  name: string;
  avatar: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  isGroup?: boolean;
  isOwner?: boolean;
  participants?: string;
  isOnline?: boolean;
}

export interface CustomFile {
  uid: string;
  name: string;
  status: string;
  url: string;
}

export interface Discussion {
  id: number;
  allowLikes: boolean;
  allowReplies: boolean;
  authorId: number;
  author_name?: string;
  content: string;
  createdAt: string;
  title: string;
  visibleToStudents: boolean;
  visibleToTeachers: boolean;
  children?: Discussion[];
}

export interface LearningTrackerCourse {
  courseId: number;
  courseName: string;
  currentWeekIndex: number;
  weeksTotal: number;
}

export interface LearningTrackerCourseWeek {
  endDate: string;
  isCurrent: boolean;
  startDate: string;
  weekIndex: number;
}

export interface LearningTrackerOverallGrade {
  letter: string;
  percent: number;
  calculationNote: string;
}

export interface LearningTrackerCourseProgress {
  percent: number;
  modulesCompleted: number;
  modulesTotal: number;
}

export interface LearningTrackerAttendance {
  ratePercent: number;
  heatmap: AttendanceHeatmap[];
}

export interface AttendanceHeatmap {
  date: string;
  status: string;
}

export interface LearningTrackerSkillMastery {
  aiToolUsage: number;
  knowledgeUnderstanding: number;
  learningEngagement: number;
  collaboration: number;
}

export interface DailyBlock {
  day: string;
  date: string;
  isToday?: boolean;
  blocks: DailyBlocks[];
}

interface DailyBlocks {
  start: string;
  end: string;
}

export interface LearningTrackerLearningHours {
  avgHoursPerDay: number;
  deltaPercentVsPrevWeek: number;
  dailyBlocks: DailyBlock[];
  isWeekComplete: boolean;
}

export interface LearningTrackerGradesTrend {
  points: GradesTrendPoints[];
}

export interface GradesTrendPoints {
  label: string;
  percent: number;
}

export interface LearningTrackerInsights {
  generatedAt?: string;
  improvementSuggestions?: string;
  weekHighlights?: string;
}

export interface ConversationItem {
  id: string;
  type: string;
  title: string;
  course_id: number;
  peer: ConversationItemPeer;
  last_message_preview: string;
  last_message_at: string;
  unread_count: number;
  my_role: string;
}

export interface ConversationItemPeer {
  id: number;
  name: string;
  avatar_url: string;
  email: string;
  user_type: string;
}

export interface MessageItem {
  id: string;
  sender: MessageItemSender;
  content: string;
  created_at: string;
  client_msg_id: string;
}

export interface MessageItemSender {
  id: number;
  name: string;
  avatar_url: string;
}

export interface MessageGroupMember {
  user: MessageGroupUser;
  role: string;
  joined_at: string;
}

export interface MessageGroupUser {
  id: string;
  name: string;
  avatar_url: string;
  email: string;
  user_type: string;
}

export interface PopularCourse {
  course_id: number;
  course_title: string;
  enrolled_count: number;
}

export interface Activity {
  id: number;
  activity: string;
  actor_id: number;
  course_id: number;
  student_id: number;
  teacher_id: number;
  metadata: string;
  created_at: string;
}

export interface DeadlineCourse {
  courseCoverUrl?: string;
  courseId: number;
  courseTitle: string;
  dueAt: string;
  id: number;
  moduleId: number;
  title: string;
  type: string;
  unitId?: number;
}

export interface PopularQuestion {
  title: string;
  course_id: number;
  course_title: string;
  course_code: string;
  asked_count: number;
  latest_asked_at: string;
}

export interface PendingGrading {
  item_id: number;
  type: string;
  title: string;
  course_id: number;
  course_title: string;
  course_code: string;
  course_cover_url: string;
  pending_count: number;
  due_at: string;
  unit_id: number;
  latest_submitted_at: string;
}

export interface KeyTimeline {
  id: number;
  type: string;
  title: string;
  course_id: number;
  course_title: string;
  course_code: string;
  course_cover_url: string;
  due_at: string;
  start_time: string;
  end_time: string;
  created_at: string;
  unit_id: number;
  description: string;
}

export interface MessageUser {
  id: number;
  name: string;
  avatar_url: string;
  email: string;
  user_type: string;
}

export type CommunityPostSortBy = 'latest' | 'most_replies' | 'most_views';

export enum CommunityPostSortByEnum {
  Latest = 'latest',
  MostReplies = 'most_replies',
  MostViews = 'most_views',
}

export const CommunityPostSortBys = [
  {
    key: 1,
    label: 'Latest',
    value: CommunityPostSortByEnum.Latest,
  },
  {
    key: 2,
    label: 'Most Replies',
    value: CommunityPostSortByEnum.MostReplies,
  },
  {
    key: 3,
    label: 'Most Views',
    value: CommunityPostSortByEnum.MostViews,
  },
];

export type CommunityPostType =
  | 'Question'
  | 'Share'
  | 'Exam'
  | 'FindPartner'
  | 'General';

export enum CommunityPostTypeEnum {
  Question = 'Question',
  Share = 'Share',
  Exam = 'Exam',
  FindPartner = 'FindPartner',
  General = 'General',
}

export const CommunityPostTypes: CommunityPostType[] = [
  CommunityPostTypeEnum.Question,
  CommunityPostTypeEnum.Share,
  CommunityPostTypeEnum.Exam,
  CommunityPostTypeEnum.FindPartner,
  CommunityPostTypeEnum.General,
];

export interface CommunityAuthorInfo {
  id: number;
  full_name: string;
  avatar: string;
  role: Role;
}

export interface Community {
  id: number;
  course_id: number;
  section_id: number;
  workspace_id: number;
  author_id: number;
  author: CommunityAuthorInfo;
  post_type: CommunityPostType;
  title: string;
  view_count: number;
  reply_count: number;
  created_at: string;
  updated_at: string;
}

export type MessageSource = 'admin' | 'teacher' | 'student';

export enum MessageSourceEnum {
  Admin = 'admin',
  Teacher = 'teacher',
  Student = 'student',
}

export interface CommonAuthorInfo {
  id: number;
  full_name: string;
  avatar?: string;
}

export interface CommonComment {
  author: CommonAuthorInfo;
  author_id: number;
  authorId?: number;
  authorName?: string;
  author_name?: string;
  children: CommonComment[];
  content: string;
  created_at: string;
  created_at_ts?: number;
  createdAtTs?: number;
  id: number;
  parent_id?: number;
  updated_at: string;
  updated_at_ts?: number;
  updatedAtTs?: number;
}

export type CommentType = 'community' | 'announcement' | 'discussion';

export enum CommentTypeEnum {
  Community = 'community',
  Announcement = 'announcement',
  Discussion = 'discussion',
}

export interface CourseAnnouncement {
  content: string;
  created_at: string;
  id: number;
  priority: RecentAnnouncementPriority;
  title: string;
}
